import { CheckCircleOutlined, ClockCircleOutlined, EyeOutlined, LogoutOutlined, PictureOutlined, PlayCircleOutlined, ReloadOutlined, SoundOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Empty, Image, message, Modal, Row, Space, Spin, Tag, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import chi_tiet_dat_ban from '../../models/chi_tiet_dat_ban';
import { AppDispatch } from '../../store';
import { logout } from '../../store/slices/auth.slices';
import quanLyBepService, { TienDoTheoBan } from '../../services/quanLyBepService';
import { hasRecipeContent, renderRecipeContent } from '../../utils/renderRecipeContent';

const { Text, Title } = Typography;
const REFRESH_INTERVAL = 8000;

const formatTableStartTime = (value: string | null) => {
  if (!value) return '--';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';

  return `${String(date.getHours()).padStart(2, '0')}h${String(date.getMinutes()).padStart(2, '0')}`;
};

const formatWaitingDuration = (createdAt: string, currentTime: number) => {
  const createdAtTime = new Date(createdAt).getTime();
  if (Number.isNaN(createdAtTime)) return '--';

  const elapsedMinutes = Math.max(0, Math.floor((currentTime - createdAtTime) / 60000));
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;

  return hours > 0 ? `${hours} giờ ${minutes} phút` : `${minutes} phút`;
};

export default function QuanLyBep() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<chi_tiet_dat_ban[]>([]);
  const [tableProgress, setTableProgress] = useState<TienDoTheoBan[]>([]);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isAudioPromptOpen, setIsAudioPromptOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);
  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);
  const [completedOrders, setCompletedOrders] = useState<chi_tiet_dat_ban[]>([]);
  const [isLoadingCompleted, setIsLoadingCompleted] = useState(false);
  const [completedError, setCompletedError] = useState<string | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<{
    dishName: string;
    content: string | null | undefined;
  } | null>(null);
  const knownOrderIdsRef = useRef<Set<number>>(new Set());
  const hasLoadedOrdersRef = useRef(false);
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioEnabledRef = useRef(false);

  const playNotificationSound = useCallback(() => {
    if (!notificationAudioRef.current) {
      notificationAudioRef.current = new Audio(
        `${process.env.PUBLIC_URL}/mp3/mon_dang_cho_che_bien.mp3`
      );
    }

    notificationAudioRef.current.currentTime = 0;
    return notificationAudioRef.current.play();
  }, []);

  const handleEnableAudio = () => {
    audioEnabledRef.current = true;
    void playNotificationSound().then(() => {
      setIsAudioEnabled(true);
      setIsAudioPromptOpen(false);
      message.success('Đã bật âm thanh thông báo');
    }).catch((playError: unknown) => {
      audioEnabledRef.current = false;
      console.error('Không thể bật âm thanh thông báo món mới:', playError);
      message.error('Không thể phát âm thanh. Vui lòng kiểm tra file âm thanh và thử lại.');
    });
  };

  const loadOrders = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsRefreshing(true);
    }

    try {
      const { orders: loadedOrders, tableProgress: loadedTableProgress } =
        await quanLyBepService.getMonAnChoBep();
      const knownOrderIds = knownOrderIdsRef.current;
      const hasNewWaitingOrder = hasLoadedOrdersRef.current && loadedOrders.some(
        (order) =>
          order.id != null &&
          order.trang_thai_mon_an?.key_value === 'DANG_CHO_CHE_BIEN' &&
          !knownOrderIds.has(order.id)
      );

      loadedOrders.forEach((order) => {
        if (order.id != null) knownOrderIds.add(order.id);
      });
      hasLoadedOrdersRef.current = true;
      setOrders(loadedOrders);
      setTableProgress(loadedTableProgress);
      setError(null);

      if (hasNewWaitingOrder && audioEnabledRef.current) {
        void playNotificationSound().catch((playError: unknown) => {
          audioEnabledRef.current = false;
          setIsAudioEnabled(false);
          console.error('Không thể phát âm thanh thông báo món mới:', playError);
          message.warning('Âm thanh bị trình duyệt chặn. Hãy bật lại âm thanh thông báo.');
        });
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Không thể tải danh sách món ăn cho bếp');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [playNotificationSound]);

  useEffect(() => {
    void loadOrders();
    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
      void loadOrders(false);
    }, REFRESH_INTERVAL);

    return () => window.clearInterval(intervalId);
  }, [loadOrders]);

  const waitingOrders = useMemo(
    () => orders.filter((order) => order.trang_thai_mon_an?.key_value === 'DANG_CHO_CHE_BIEN'),
    [orders]
  );
  const cookingOrders = useMemo(
    () => orders.filter((order) => order.trang_thai_mon_an?.key_value === 'DANG_CHE_BIEN'),
    [orders]
  );

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const loadCompletedOrders = async () => {
    setIsLoadingCompleted(true);
    try {
      setCompletedOrders(await quanLyBepService.getMonAnDaCheBienXong());
      setCompletedError(null);
    } catch (loadError) {
      setCompletedError(
        loadError instanceof Error ? loadError.message : 'Không thể tải danh sách món đã chế biến xong'
      );
    } finally {
      setIsLoadingCompleted(false);
    }
  };

  const openCompletedOrders = () => {
    setIsCompletedModalOpen(true);
    void loadCompletedOrders();
  };

  const handleStartCooking = async (order: chi_tiet_dat_ban) => {
    if (order.id == null || updatingOrderId !== null) return;

    setUpdatingOrderId(order.id);
    try {
      const updatedOrder = await quanLyBepService.batDauCheBien(order.id);
      setOrders((previousOrders) =>
        previousOrders.map((item) => item.id === updatedOrder.id ? updatedOrder : item)
      );
      setError(null);
      message.success('Đã chuyển món sang trạng thái đang chế biến');
    } catch (updateError) {
      message.error(updateError instanceof Error ? updateError.message : 'Không thể cập nhật trạng thái món ăn');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleCompleteCooking = async (order: chi_tiet_dat_ban) => {
    if (order.id == null || updatingOrderId !== null) return;

    setUpdatingOrderId(order.id);
    try {
      const updatedOrder = await quanLyBepService.hoanTatCheBien(order.id);
      setOrders((previousOrders) =>
        previousOrders.map((item) => item.id === updatedOrder.id ? updatedOrder : item)
      );
      void loadOrders(false);
      setError(null);
      message.success('Đã đánh dấu món chế biến xong');
    } catch (updateError) {
      message.error(updateError instanceof Error ? updateError.message : 'Không thể cập nhật trạng thái món ăn');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const renderOrder = (
    order: chi_tiet_dat_ban,
    canStartCooking = false,
    canCompleteCooking = false,
    showCompletedAt = false
  ) => {
    const dish = order.hang_hoa;
    const tableName = order.dat_ban?.ban?.ten || 'Chưa xác định bàn';
    const imageUrl = dish?.hinh_anh_hang_hoas?.find((image) => image.full_path)?.full_path;

    return (
      <Card key={order.id} size="small" className="kitchen-order-card" style={{ marginBottom: 12 }}>
        <div className="kitchen-order-main">
          <Space align="start" size="middle" className="kitchen-order-info">
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={dish?.ten || 'Hình món ăn'}
                width={84}
                height={84}
                className="kitchen-order-image"
                style={{ objectFit: 'cover', borderRadius: 8 }}
                preview={false}
              />
            ) : (
              <div
                className="kitchen-order-image kitchen-order-image-placeholder"
                aria-label="Không có hình món ăn"
                style={{
                  width: 84,
                  height: 84,
                  display: 'grid',
                  placeItems: 'center',
                  background: '#f5f5f5',
                  borderRadius: 8,
                  color: '#8c8c8c',
                }}
              >
                <PictureOutlined style={{ fontSize: 28 }} />
              </div>
            )}
            <Space direction="vertical" size={4} className="kitchen-order-details">
              <Space size={4} className="kitchen-order-title">
                <Text strong style={{ fontSize: 16 }}>{dish?.ten || 'Món ăn không còn tồn tại'}</Text>
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined style={{ fontSize: '200%' }} />}
                  aria-label={`Xem nguyên liệu và cách chế biến: ${dish?.ten || 'Món ăn'}`}
                  title="Xem nguyên liệu và cách chế biến"
                  onClick={() => setSelectedRecipe({
                    dishName: dish?.ten || 'Món ăn',
                    content: dish?.nguyen_lieu_cach_che_bien,
                  })}
                />
              </Space>
              <Text>Bàn: <Text strong>{tableName}</Text></Text>
              {order.created_at && (
                <Text type="secondary">
                  <ClockCircleOutlined /> Giờ đặt món: {new Date(order.created_at).toLocaleTimeString('vi-VN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })} | Thời gian chờ: {formatWaitingDuration(order.created_at, currentTime)}
                </Text>
              )}
              <Text>
                Số lượng:{' '}
                <Tag color={order.so_luong >= 2 ? 'red' : 'blue'} style={{ fontSize: 16, fontWeight: 700, padding: '2px 8px', lineHeight: 1.6 }}>
                  {order.so_luong}
                </Tag>
              </Text>
              {order.ghi_chu && (
                <div
                  className="kitchen-order-note"
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: 6,
                    background: '#fff7e6',
                    border: '1px solid #ffd591',
                    color: '#ad6800',
                    fontWeight: 600,
                    maxWidth: '100%',
                  }}
                >
                  Ghi chú: {order.ghi_chu}
                </div>
              )}
              {showCompletedAt && order.updated_at && (
                <Text type="secondary">
                  <ClockCircleOutlined /> Hoàn tất lúc: {new Date(order.updated_at).toLocaleString('vi-VN')}
                </Text>
              )}
            </Space>
          </Space>
          {(canStartCooking || canCompleteCooking) && (
            <div className="kitchen-order-action">
              {canStartCooking && (
                <Button
                  type="primary"
                  size="large"
                  icon={<PlayCircleOutlined />}
                  loading={updatingOrderId === order.id}
                  disabled={updatingOrderId !== null && updatingOrderId !== order.id}
                  onClick={() => void handleStartCooking(order)}
                >
                  Bắt đầu chế biến
                </Button>
              )}
              {canCompleteCooking && (
                <Button
                  danger
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  loading={updatingOrderId === order.id}
                  disabled={updatingOrderId !== null && updatingOrderId !== order.id}
                  onClick={() => void handleCompleteCooking(order)}
                >
                  Món chế biến xong
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>
    );
  };

  const recipeContent = selectedRecipe?.content
    ? renderRecipeContent(selectedRecipe.content)
    : [];
  const selectedRecipeHasContent = hasRecipeContent(selectedRecipe?.content);

  return (
    <main className="kitchen-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Modal
          title="Bật âm thanh thông báo"
          className="kitchen-modal"
          open={isAudioPromptOpen}
          closable={false}
          maskClosable={false}
          keyboard={false}
          footer={[
            <Button key="disable-audio" onClick={() => setIsAudioPromptOpen(false)}>
              Không bật âm thanh
            </Button>,
            <Button key="enable-audio" type="primary" icon={<SoundOutlined />} onClick={handleEnableAudio}>
              Bật âm thanh
            </Button>,
          ]}
        >
          <Text>Cho phép phát âm thanh khi có món mới đang chờ chế biến.</Text>
        </Modal>
        <Modal
          title={`Nguyên liệu và cách chế biến${selectedRecipe ? ` - ${selectedRecipe.dishName}` : ''}`}
          className="kitchen-modal"
          open={selectedRecipe !== null}
          zIndex={1100}
          onCancel={() => setSelectedRecipe(null)}
          footer={[
            <Button key="close" onClick={() => setSelectedRecipe(null)}>
              Đóng
            </Button>,
          ]}
        >
          {selectedRecipeHasContent ? (
            <div className="recipe-content">{recipeContent}</div>
          ) : (
            <Empty description="Chưa có thông tin nguyên liệu và cách chế biến" />
          )}
        </Modal>
        <Modal
          title={`Món đã chế biến xong (${completedOrders.length})`}
          className="kitchen-modal kitchen-completed-modal"
          open={isCompletedModalOpen}
          onCancel={() => setIsCompletedModalOpen(false)}
          footer={[
            <Button
              key="refresh"
              icon={<ReloadOutlined />}
              loading={isLoadingCompleted}
              onClick={() => void loadCompletedOrders()}
            >
              Làm mới
            </Button>,
            <Button key="close" onClick={() => setIsCompletedModalOpen(false)}>
              Đóng
            </Button>,
          ]}
          width={800}
        >
          {completedError && (
            <Alert type="error" showIcon message={completedError} style={{ marginBottom: 16 }} />
          )}
          <Spin spinning={isLoadingCompleted}>
            {completedOrders.length > 0 ? (
              completedOrders.map((order) => renderOrder(order, false, false, true))
            ) : (
              !isLoadingCompleted && <Empty description="Chưa có món nào chế biến xong" />
            )}
          </Spin>
        </Modal>
        <div className="kitchen-header">
          <div className="kitchen-heading">
            <Title level={2} style={{ margin: 0 }}>Quản lý bếp</Title>
            <Text type="secondary">Danh sách món đang chờ và đang chế biến</Text>
          </div>
          <div className="kitchen-actions">
            {isAudioEnabled ? (
              <Text type="success"><SoundOutlined /> Âm thanh đã bật</Text>
            ) : (
              <Button type="primary" ghost icon={<SoundOutlined />} onClick={handleEnableAudio}>
                Bật âm thanh
              </Button>
            )}
            <Button icon={<ReloadOutlined />} loading={isRefreshing} onClick={() => void loadOrders()}>
              Làm mới
            </Button>
            <Button icon={<UnorderedListOutlined />} onClick={() => navigate('/quan-ly-bep/danh-sach-mon-an')}>
              Xem danh sách món ăn
            </Button>
            <Button icon={<EyeOutlined style={{ fontSize: '200%' }} />} onClick={openCompletedOrders}>
              Xem món đã chế biến xong
            </Button>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>Đăng xuất</Button>
          </div>
        </div>

        {error && <Alert type="error" showIcon message={error} />}

        {tableProgress.length > 0 && (
          <Space wrap size={[8, 8]} className="kitchen-table-progress" aria-label="Tiến độ món ăn theo bàn (giờ vào bàn | món đang làm | số lượng món làm xong | tổng số món)">
            <Text strong>Tiến độ món theo bàn: (giờ vào bàn | món đang làm | số lượng món làm xong | tổng số món)</Text>
            {tableProgress.map((progress) => {
              const startTime = formatTableStartTime(progress.gio_vao_ban);
              const label = `${progress.ten_ban} (${startTime} | ${progress.so_luong_hoan_tat}/${progress.so_luong_dang_che_bien}/${progress.tong_so_luong})`;

              return (
                <Tag key={progress.ban_id} aria-label={label} style={{ marginInlineEnd: 0 }}>
                  <Text strong>{progress.ten_ban} ({startTime} | </Text>
                  <Text strong style={{ color: '#1677ff' }}>{progress.so_luong_hoan_tat}</Text>
                  <Text strong>/</Text>
                  <Text strong style={{ color: '#fa8c16' }}>{progress.so_luong_dang_che_bien}</Text>
                  <Text strong>/</Text>
                  <Text strong style={{ color: '#000000' }}>{progress.tong_so_luong}</Text>
                  <Text strong>) </Text>
                </Tag>
              );
            })}
          </Space>
        )}

        <Spin spinning={isLoading}>
          <Row gutter={[16, 16]} align="top">
            <Col xs={24} md={12}>
              <Card title={`Đang chờ chế biến (${waitingOrders.length})`}>
                {waitingOrders.length > 0 ? (
                  waitingOrders.map((order) => renderOrder(order, true))
                ) : (
                  <Empty description="Không có món đang chờ chế biến" />
                )}
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title={`Đang chế biến (${cookingOrders.length})`}>
                {cookingOrders.length > 0 ? (
                  cookingOrders.map((order) => renderOrder(order, false, true))
                ) : (
                  <Empty description="Không có món đang chế biến" />
                )}
              </Card>
            </Col>
          </Row>
        </Spin>
      </Space>
    </main>
  );
}
